"use client";

import { DEFAULT_LOGIN_REDIRECT } from "@app/commonRoutes";
import { config } from "@app/config";
import { REDIRECT_COOKIE, SESSION_COOKIE } from "@app/consts";
import { Session } from "@app/types";
import { getBrandingImage, getBrandingImageSrc } from "@app/utils/helpers/getBrandingImage";
import { useRouter } from "@app/utils/navigation";
import { loginUser } from "@ea/api_client/api/endpoints/user";
import ClientUserError from "@ea/shared_types/ea.errors";
import { EA_ERROR_CODES, PUBLIC_SETTINGS_KEYS } from "@ea/shared_types/next/ea.enums";
import { UserSchemas } from "@ea/shared_types/next/ea.schema";
import { UserTypes } from "@ea/shared_types/next/ea.types";
import { Box } from "@ea/ui/components/Box";
import { FormInputsWrapper } from "@ea/ui/components/forms/FormInputsWrapper";
import { FormSubmitButton } from "@ea/ui/components/forms/FormSubmitButton";
import { FormWrapper } from "@ea/ui/components/forms/FormWrapper";
import { useOverlayStore } from "@ea/ui/components/OverlayManager/useOverlayStore";
import { Paper } from "@ea/ui/components/Paper";
import { DataTestIds } from "@ea/ui/dataTestIds";
import { useGetPublicSettings } from "@ea/ui/hooks/api/useInternalSettings";
import { useNotification } from "@ea/ui/hooks/useNotification";
import { NavigateNextOutlinedIcon } from "@ea/ui/icons/NavigateNextOutlinedIcon";
import { useTranslations } from "@ea/ui/translations/useTranslations";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";
import Image from "next/image";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { ConfirmLoginDialog } from "./ConfirmLoginDialog";
import {
  LoginDecoration,
  LoginFormContainer,
  LoginPageContainer,
  SSOLink,
} from "./LoginPage.styles";
import { LoginFormFields } from "./LoginFormFields";
import { useBrand } from "@ea/ui/contexts/BrandContext";
import { Typography } from "@ea/ui/components/Typography";

export const LoginPage = ({ error }: { error?: string }) => {
  const { toast } = useNotification();
  const { open } = useOverlayStore();
  const router = useRouter();
  const [serverError, setServerError] = useState("");
  const queryClient = useQueryClient();
  const { appVariant } = useBrand();

  const { data: publicSettings } = useGetPublicSettings();

  const enabled = useMemo(
    () => ({
      saml: publicSettings?.find((s) => s.id === PUBLIC_SETTINGS_KEYS.SSO_SAML_ENABLED)?.value,
      azure: publicSettings?.find((s) => s.id === PUBLIC_SETTINGS_KEYS.SSO_AZURE_ENABLED)?.value,
    }),
    [publicSettings],
  );

  const methods = useForm({
    resolver: zodResolver(UserSchemas["login"]),
  });

  const { t } = useTranslations();

  const renderLogo = getBrandingImage({
    appVariant: appVariant,
    location: "LoginLogo",
  });

  const handleFormSubmit = async (data: UserTypes["login"]) => {
    const { password, username, withLogout } = data;
    try {
      const response = await loginUser({ username, password, withLogout });

      if (!response?.error) {
        const user = response.user;
        const session: Session = {
          user: {
            id: user.id,
            username: user.username,
            email: user.email,
            roles: user.roles,
            sso: user.sso,
          },
          authToken: response.id,
        };

        Cookies.set(SESSION_COOKIE, JSON.stringify(session), {
          expires: 180,
        });

        const redirectUrl = Cookies.get(REDIRECT_COOKIE) || DEFAULT_LOGIN_REDIRECT;
        Cookies.remove(REDIRECT_COOKIE, { path: "/" });

        await router.push(redirectUrl, {
          locale: user?.language?.toLowerCase(),
        });
        return;
      } else {
        toast.error(t("Toast.somethingWentWrong"), { error: response?.error });
      }
    } catch (error) {
      if (error instanceof ClientUserError) {
        if (error.code === EA_ERROR_CODES.USER_MULTI_SESSSION_BLOCK) {
          open(ConfirmLoginDialog, {
            onConfirm: () => {
              queryClient.clear();
              handleFormSubmit({ username, password, withLogout: true });
            },
          });
          return;
        }

        if (error?.code === EA_ERROR_CODES.USER_ACCOUNT_IS_INACTIVE) {
          setServerError(t("Login.inactveUser"));
          return;
        }

        if (error?.code === EA_ERROR_CODES.USER_INVALID_CREDENTIALS) {
          setServerError(t("Login.error"));
          return;
        }
      }
      toast.error(t("Toast.somethingWentWrong"), { error });
    }
  };

  useEffect(() => {
    if (error) {
      setServerError(t("Login.errorMessage", { error }));
    }
  }, [error, t]);

  const MSLink = ({ href, label }: { href: string; label: string }) => (
    <SSOLink href={href}>
      <Image src="/img/ms-symbol.svg" alt="Microsoft" width={24} height={24} />
      <Typography component="span" fontWeight={600} fontFamily={"Segoe UI"}>
        {label}
      </Typography>
    </SSOLink>
  );

  return (
    <LoginPageContainer>
      <Image src={getBrandingImageSrc(appVariant)} fill={true} alt="" style={{ zIndex: -1 }} />
      <LoginFormContainer maxWidth={"720px"}>
        <Box mb={2} width={"100%"}>
          {renderLogo}
        </Box>
        <Paper
          elevation={6}
          sx={{ paddingBottom: "2rem", borderRadius: "2rem", width: "75%", overflow: "hidden" }}
        >
          <LoginDecoration />
          <FormWrapper
            methods={methods}
            onSubmit={handleFormSubmit}
            dataTestid={DataTestIds.FORM_LOGIN}
            stackProps={{ px: 0 }}
          >
            <FormInputsWrapper px={"3rem"} gap={2}>
              <FormInputsWrapper alignItems="center">
                <LoginFormFields serverError={serverError} />
              </FormInputsWrapper>

              <FormSubmitButton endIcon={<NavigateNextOutlinedIcon />}>
                {t("Login.login")}
              </FormSubmitButton>

              <FormInputsWrapper direction={"row"} justifyContent="space-between">
                {enabled.azure && (
                  <MSLink
                    href={`${config.getApiPath()}/auth/microsoft`}
                    label={t("Login.sso.microsoft")}
                  />
                )}
                {enabled.saml && (
                  <MSLink href={`${config.getApiPath()}/saml`} label={t("Login.sso.saml")} />
                )}
              </FormInputsWrapper>
            </FormInputsWrapper>
          </FormWrapper>
        </Paper>
      </LoginFormContainer>
    </LoginPageContainer>
  );
};
